import { galleryItemKind } from "./gallery.kind";
import type { GalleryImage, GalleryPhoto } from "./gallery";

/** One stop in the lightbox: a photo, and the tile it belongs to. */
export interface GalleryPhotoStop {
  photo: GalleryPhoto;
  imageIndex: number;
  photoIndex: number;
}

/**
 * What the lightbox pages through at one time.
 *
 * A photo set is its own group; every loose tile (a single photo, a
 * comparison) shares one group, in tile order. Paging the whole section as one
 * sequence meant opening a photo of one project and scrolling into another
 * project's photos, under a count and a thumbnail strip describing neither.
 * The group is the scope of the arrows, the counter and the thumbnails, so a
 * visitor is always inside exactly one project — or outside all of them.
 */
export interface GalleryGroup {
  /** "set" is one project's photos; "photos" is every loose tile. */
  kind: "set" | "photos";
  /** The group's own name, "" when it has none: the loose group is not a
   *  project, and a set is only named if a pro wrote something short on it.
   *  What to call an unnamed group is the lightbox's business, not this. */
  label: string;
  stops: GalleryPhotoStop[];
}

/**
 * Past this a label is a sentence, not a name. Alt text is written to describe
 * a photo to a screen reader ("A waterfall cascades into a moss-covered canyon
 * surrounded by green hills and vegetation"), which reads as nonsense where a
 * project's name belongs, so a set that long-winded is treated as unnamed.
 */
const MAX_SET_LABEL = 40;

/**
 * Every photo for one tile, cover first. The cover is always index 0 so the lightbox
 * opens on the image that was actually clicked.
 */
export function getProjectPhotos(image: GalleryImage): GalleryPhoto[] {
  const cover = image.imageUrl || image.afterImageUrl || image.beforeImageUrl;
  // A photo is one photo: extra photos belong to a photo set, and paging
  // through leftovers a pro can no longer see in the panel would be a ghost.
  const rest =
    galleryItemKind(image) === "photo"
      ? []
      : (image.photos ?? []).filter((photo) => photo?.imageUrl);

  if (!cover) {
    return rest;
  }

  return [
    { imageUrl: cover, alt: image.alt, caption: image.caption },
    ...rest.filter((photo) => photo.imageUrl !== cover),
  ];
}

/** A set's own name, or "" when nothing on it reads as one. */
function setLabel(image: GalleryImage): string {
  for (const candidate of [image.caption, image.tag, image.alt]) {
    const text = candidate?.trim();
    if (text && text.length <= MAX_SET_LABEL) {
      return text;
    }
  }
  return "";
}

/**
 * The section's photos as groups, in tile order. The loose group takes the
 * position of the first loose tile, so a gallery that opens with single photos
 * lists them first and one that opens with a project lists that project first.
 */
export function photoGroupsOf(images: GalleryImage[]): GalleryGroup[] {
  const groups: GalleryGroup[] = [];
  let loose: GalleryGroup | null = null;

  images.forEach((image, imageIndex) => {
    const photos = getProjectPhotos(image);
    if (photos.length === 0) {
      return;
    }
    const stops = photos.map((photo, photoIndex) => ({ photo, imageIndex, photoIndex }));

    if (galleryItemKind(image) === "project") {
      groups.push({ kind: "set", label: setLabel(image), stops });
      return;
    }

    if (!loose) {
      loose = { kind: "photos", label: "", stops: [] };
      groups.push(loose);
    }
    loose.stops.push(...stops);
  });

  return groups;
}

/**
 * One screen of the lightbox: a photo of a group, or the card that closes a
 * group and names the next one. The end card only exists when there IS a next
 * group, so a gallery that is one project (or all loose photos) pages exactly
 * as it did before groups.
 */
export type GalleryView =
  | { kind: "photo"; groupIndex: number; stopIndex: number }
  | { kind: "end"; groupIndex: number; nextGroupIndex: number };

/** Every view in order: each group's photos, then its end card. */
export function galleryViewsOf(groups: GalleryGroup[]): GalleryView[] {
  const hasEndCards = groups.length > 1;
  return groups.flatMap((group, groupIndex) => {
    const photos = group.stops.map(
      (_, stopIndex): GalleryView => ({ kind: "photo", groupIndex, stopIndex }),
    );
    if (!hasEndCards) {
      return photos;
    }
    return [
      ...photos,
      {
        kind: "end" as const,
        groupIndex,
        // The last group's card points back at the first: paging is a loop
        // already, and an end that offers nothing is a dead end.
        nextGroupIndex: (groupIndex + 1) % groups.length,
      },
    ];
  });
}

/** The view a click on tile `imageIndex` opens: that tile's own photo. */
export function viewIndexForImage(views: GalleryView[], groups: GalleryGroup[], imageIndex: number): number {
  return views.findIndex(
    (view) =>
      view.kind === "photo" &&
      groups[view.groupIndex]?.stops[view.stopIndex]?.imageIndex === imageIndex &&
      groups[view.groupIndex]?.stops[view.stopIndex]?.photoIndex === 0,
  );
}

/** The view showing stop `stopIndex` of group `groupIndex` (thumbnail clicks). */
export function viewIndexForStop(
  views: GalleryView[],
  groupIndex: number,
  stopIndex: number,
): number {
  return views.findIndex(
    (view) =>
      view.kind === "photo" && view.groupIndex === groupIndex && view.stopIndex === stopIndex,
  );
}
